import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  TrendingUp,
  Users,
  Clock,
  Package,
  DollarSign,
  Bell,
  Filter,
  Download,
  Settings
} from 'lucide-react';
import { SmartDropdown, useToast, LoadingState } from '../components/ui';
import SliceAndDicePanel from '../components/analytics/SliceAndDicePanel';
import OperationsAnalyticsTab from '../components/analytics/OperationsAnalyticsTab';
import SupplierPerformanceTab from '../components/analytics/SupplierPerformanceTab';
import SLAComplianceTab from '../components/analytics/SLAComplianceTab';
import MaterialAnalyticsTab from '../components/analytics/MaterialAnalyticsTab';
import CostAnalysisTab from '../components/analytics/CostAnalysisTab';
import AlertsNotificationsTab from '../components/analytics/AlertsNotificationsTab';
import useAnalyticsStore from '../stores/analyticsStore';
import { exportAnalyticsData } from '../services/analyticsService';

const AdvancedAnalytics = () => {
  const { success, error: showError } = useToast();

  // Zustand store
  const {
    activeTab,
    setActiveTab,
    dateRange,
    setDateRange,
    showFilters,
    toggleFilters,
    appliedFilters
  } = useAnalyticsStore();

  // Tabs configuration
  const tabs = [
    { id: 'operations', label: 'Operations Analytics', icon: BarChart3, color: 'blue' },
    { id: 'suppliers', label: 'Supplier Performance', icon: Users, color: 'purple' },
    { id: 'sla', label: 'SLA Compliance', icon: Clock, color: 'green' },
    { id: 'materials', label: 'Material Analytics', icon: Package, color: 'yellow' },
    { id: 'costs', label: 'Cost Analysis', icon: DollarSign, color: 'red' },
    { id: 'alerts', label: 'Alerts & Notifications', icon: Bell, color: 'orange' }
  ];

  const getTabColorClasses = (color, isActive) => {
    const colors = {
      blue: isActive ? 'border-blue-500 text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:border-gray-300',
      purple: isActive ? 'border-purple-500 text-purple-600 bg-purple-50' : 'text-gray-600 hover:text-purple-600 hover:border-gray-300',
      green: isActive ? 'border-green-500 text-green-600 bg-green-50' : 'text-gray-600 hover:text-green-600 hover:border-gray-300',
      yellow: isActive ? 'border-yellow-500 text-yellow-600 bg-yellow-50' : 'text-gray-600 hover:text-yellow-600 hover:border-gray-300',
      red: isActive ? 'border-red-500 text-red-600 bg-red-50' : 'text-gray-600 hover:text-red-600 hover:border-gray-300',
      orange: isActive ? 'border-orange-500 text-orange-600 bg-orange-50' : 'text-gray-600 hover:text-orange-600 hover:border-gray-300',
    };
    return colors[color];
  };

  const handleExportData = () => {
    const data = exportAnalyticsData(activeTab, appliedFilters, dateRange);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advanced-analytics-${activeTab}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    success('Analytics data exported successfully!');
  };

  const handleFiltersApplied = (filters) => {
    // Callback when filters are applied - can trigger data refresh here
    console.log('Filters applied:', filters);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics & Alerts</h1>
          <p className="text-gray-600 mt-2">Multi-dimensional analysis with intelligent alerting</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <SmartDropdown
            value={dateRange}
            onChange={setDateRange}
            options={[
              { value: '7', label: 'Last 7 Days' },
              { value: '14', label: 'Last 14 Days' },
              { value: '30', label: 'Last 30 Days' },
              { value: '60', label: 'Last 60 Days' },
              { value: '90', label: 'Last 90 Days' },
              { value: 'custom', label: 'Custom Range' }
            ]}
            className="w-48"
          />

          {/* Action Buttons */}
          <button
            onClick={toggleFilters}
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              showFilters
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>

          <button
            onClick={handleExportData}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>

          {activeTab === 'alerts' && (
            <button
              onClick={() => {/* TODO: Open alert configuration */}}
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors"
            >
              <Settings className="h-4 w-4 mr-2" />
              Configure Alerts
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-all
                  ${getTabColorClasses(tab.color, isActive)}
                  ${!isActive && 'border-transparent'}
                `}
              >
                <Icon className={`h-5 w-5 mr-2 ${isActive ? '' : 'opacity-60'}`} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Slice & Dice Filter Panel */}
      <SliceAndDicePanel onApply={handleFiltersApplied} />

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'operations' && <OperationsAnalyticsTab />}
        {activeTab === 'suppliers' && <SupplierPerformanceTab />}
        {activeTab === 'sla' && <SLAComplianceTab />}
        {activeTab === 'materials' && <MaterialAnalyticsTab />}
        {activeTab === 'costs' && <CostAnalysisTab />}
        {activeTab === 'alerts' && <AlertsNotificationsTab />}
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
